// store.ts
import type {
  AnyAction,
  Middleware,
  MiddlewareAPI,
  ThunkAction,
  ThunkDispatch,
} from "@reduxjs/toolkit";
import { combineSlices, configureStore, createAction, isRejectedWithValue } from "@reduxjs/toolkit";
import { setupListeners } from "@reduxjs/toolkit/query";

// Slices / RTK Query
import { designSlice } from "../DesignTab/designSlice";
import { apiRTK } from "../apiRTK";
import { deviceSlice } from "../DesignTab/devices/deviceSlice";
import { roomSlice } from "../Room/RoomSlice";
import "../Room/roomapi.enhancer";
import { McBimSlice } from "../McBim/McBimSlice";
import { debugSlice } from "../Debug/DebugSlice";
import { counterSlice } from "../counterSlice";
import undoable from "redux-undo";
import { makePatchSpyMiddleware } from "../patchSpyMiddleware";
import { globalSlice } from "../globalSlice";

// ag-Grid state slice + LS-middleware
import { agGridStateSlice } from "../agGridStateSlice";
import {
  agGridLocalStorageMiddleware,
  loadAgGridStateFromLS,
} from "../agGridStateLocalStorageMiddleware";
import { trainingSlice } from "../trainingdata/trainingSlice";
import { deviceHistoryReducer } from "../DesignTab/history/deviceHistorySlice";
import { releaseNotesPublicSlice } from "../Releasenotes/releaseNotesPublicSlice";
import { viewerSlice } from "../ViewerRedux/viewerSlice";
import { pnsSelectionSlice } from "../DesignTab/routing/pnsSelectionSlice";
import { unsavedRoutingSlice } from "../DesignTab/routing/unsavedRoutingSlice";
import { pipeRoutingSlice } from "../DesignTab/pipeRoutingSlice";
import { standardRoomSlice } from "../RoomSideBar/StandardRoom/standardRoomSlice";
import { ceilingSlice } from "../RoomSideBar/MultiplacerCeilingDevices/ceilingSlice";
import { drofusForekomsterSlice } from "../DrofusForekomster/drofusForekomsterSlice";

export const resetReduxForProjectChange = createAction<number>("app/resetReduxForProjectChange");

// --- Root reducer -----------------------------------------------------------
const appReducer = combineSlices({
  global: globalSlice.reducer,
  design: designSlice.reducer,
  api: apiRTK.reducer,
  device: deviceSlice.reducer,
  room: roomSlice.reducer,
  mcbim: McBimSlice.reducer,
  debug: debugSlice.reducer,
  training: trainingSlice.reducer,
  deviceHistory: deviceHistoryReducer, // ✅ NY
  counter: undoable(counterSlice.reducer),
  releaseNotesPublic: releaseNotesPublicSlice.reducer,
  viewer: viewerSlice.reducer,
  agGrid: agGridStateSlice.reducer, // ⬅︎ matchar selectAgGridStateByKey
  unsavedRouting: unsavedRoutingSlice.reducer,
  pnsSelection: pnsSelectionSlice.reducer,
  pipeRouting: pipeRoutingSlice.reducer,
  standardRoom: standardRoomSlice.reducer,
  ceiling: ceilingSlice.reducer,
  drofusForekomster: drofusForekomsterSlice.reducer,
});

export type IRootState = ReturnType<typeof appReducer>;

const rootReducer = (state: IRootState | Partial<IRootState> | undefined, action: AnyAction) => {
  if (resetReduxForProjectChange.match(action)) {
    const preservedState = {
      global: state?.global,
      agGrid: state?.agGrid,
      releaseNotesPublic: state?.releaseNotesPublic,
    } as Partial<IRootState> as IRootState;

    const nextState = appReducer(preservedState, action);

    return {
      ...nextState,
      design: {
        ...nextState.design,
        projectNo: action.payload,
      },
    };
  }

  return appReducer(state, action);
};

const __DEV__ =
  typeof import.meta !== "undefined" ? import.meta.env.DEV : process.env.NODE_ENV !== "production";

// --- Optional: tyst logger för rejected actions -----------------------------
const rtkQueryErrorLogger: Middleware = (_api: MiddlewareAPI) => (next) => (action) => {
  if (isRejectedWithValue(action)) {
    // console.warn("Rejected action:", action);
  }
  return next(action);
};

// --- Store factory ----------------------------------------------------------
export const makeStore = (preloadedState?: Partial<IRootState>) => {
  const baseMiddleware = (getDefaultMiddleware: any) =>
    getDefaultMiddleware().concat(
      rtkQueryErrorLogger,
      apiRTK.middleware,
      agGridLocalStorageMiddleware // ⬅︎ spegla agGrid.byKey → LocalStorage
    );

  // Hydrera från LS om inget preloadedState givits (eller om agGrid saknas)
  const hydrated = preloadedState ?? loadAgGridStateFromLS();

  const store = configureStore({
    reducer: rootReducer,
    middleware: (gdm) =>
      __DEV__ ? baseMiddleware(gdm).concat(makePatchSpyMiddleware(apiRTK)) : baseMiddleware(gdm),
    preloadedState: hydrated,
  });

  setupListeners(store.dispatch);
  return store;
};

// --- Singleton store + typer ------------------------------------------------
export const store = makeStore();
export type AppStore = typeof store;

// Viktigt: ge dispatch thunk-förmåga
export type AppDispatch = ThunkDispatch<IRootState, unknown, AnyAction>;

// Thunk-typ för dina egna thunks
export type IAppThunk<TReturn = void> = ThunkAction<TReturn, IRootState, unknown, AnyAction>;
